import { CalendarClock } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { UnscheduledLesson } from "../../../api/schemas.js";
import { DialogShell } from "../../../ui/DialogShell.js";
import { EmptyState } from "../../../ui/EmptyState.js";

export interface UnscheduledQueueDialogProps {
  readonly open: boolean;
  readonly lessons: readonly UnscheduledLesson[];
  readonly onClose: () => void;
  readonly onPick: (lesson: UnscheduledLesson) => void;
}

/** «7 уроков», «2 урока», «1 урок» — счётчик читается вслух, значит склоняется. */
export function lessonWord(count: number): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod100 >= 11 && mod100 <= 14) return "уроков";
  if (mod10 === 1) return "урок";
  if (mod10 >= 2 && mod10 <= 4) return "урока";
  return "уроков";
}

function progress(lesson: UnscheduledLesson): string {
  return lesson.courseTotalLessons
    ? `урок ${lesson.lessonOrder} из ${lesson.courseTotalLessons}`
    : `урок ${lesson.lessonOrder}`;
}

/**
 * Очередь уроков, которым инструкторы передали запись менеджеру.
 *
 * Раньше счётчик в шапке показывал N, а по нажатию открывал форму для первого попавшегося: в
 * общем календаре эти N принадлежат разным клиентам и разным сделкам, и менеджер оказывался в
 * форме случайного. Счётчик, показывающий N, должен показывать N — это и есть переданная ему
 * работа, увидеть её целиком было больше негде.
 */
export function UnscheduledQueueDialog(props: UnscheduledQueueDialogProps) {
  const count = props.lessons.length;
  return (
    <DialogShell
      open={props.open}
      title={`Уроки без времени: ${count}`}
      testId="unscheduled-queue"
      onClose={props.onClose}
    >
      {count === 0 ? (
        <EmptyState
          title="Очередь пуста"
          description="Все уроки, переданные инструкторами, уже назначены."
          testId="unscheduled-queue-empty"
        />
      ) : (
        <ul className="flex flex-col gap-2">
          {props.lessons.map((lesson) => (
            <li key={lesson.lessonId}>
              <Button
                type="button"
                variant="outline"
                className="h-auto w-full justify-start gap-3 px-3 py-3 text-left"
                data-testid={`unscheduled-queue-item-${lesson.lessonId}`}
                onClick={() => props.onPick(lesson)}
              >
                <CalendarClock className="size-4 shrink-0 text-ink-500" aria-hidden="true" />
                <span className="flex min-w-0 flex-col gap-0.5">
                  <span className="min-w-0 truncate font-semibold">
                    {lesson.clientName ?? "Клиент не указан"}
                  </span>
                  <span className="pwz-readout min-w-0 truncate text-ink-500 text-xs">
                    {[lesson.courseCode ?? lesson.courseTitle, progress(lesson)]
                      .filter(Boolean)
                      .join(" · ")}
                    {` · ${lesson.durationMinutes} мин`}
                  </span>
                </span>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </DialogShell>
  );
}
